import { Fragment, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { fetchProfile, type ProfileView } from '../api/profiles';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { Avatar } from './IdentityPanel';
import { Planned, PlannedChip } from './Planned';
import { PageLoading, PageProblem } from './PageStates';
import { useRetryKey } from './connectionHelpers';
import { Shell } from './Shell';
import { errorText } from './useSessionLive';
import { fmtDay as fmtShortDay } from './dates';

type Tab = 'played' | 'made' | 'medals' | 'history';

/**
 * Medals on the profile: the header's figure and the tab. Off for now, and
 * only hidden — nothing awards a medal a player has seen yet, so the page
 * showed a zero and an empty tab to everybody. Whether they come back, and as
 * what, is decided at roadmap step R5 (docs/design/ROADMAP.md); this one
 * constant is the whole switch.
 */
const SHOW_MEDALS: boolean = false;

const TAB_ICONS: Record<Tab, string> = {
  played: 'play-circle',
  made: 'pencil-square',
  medals: 'award',
  history: 'clock-history',
};

const fmtTime = (seconds: number) =>
  ca.profilePage.timePlayed(Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60));
const fmtDate = (iso: string) => fmtShortDay(iso);
const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('ca', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
const fmtClock = (iso: string) =>
  new Date(iso).toLocaleTimeString('ca', { hour: '2-digit', minute: '2-digit' });

/** The history in the order the platform sent it, cut into the days a person reads it by. */
function byDay<T extends { occurredAt: string }>(entries: T[]): [string, T[]][] {
  const days = new Map<string, T[]>();
  for (const entry of entries) {
    const key = fmtDay(entry.occurredAt);
    const day = days.get(key) ?? [];
    day.push(entry);
    days.set(key, day);
  }
  return [...days.entries()];
}

const VISIBILITY_CHIP: Record<string, string> = { PUBLIC: 'ok', UNLISTED: 'warn', PRIVATE: '' };

/** A member's profile (UI doc 5.9): played, made, medals, history; the owner's settings live at /settings. */
export function Profile() {
  const { handle = '' } = useParams();
  const { me } = useAuth();
  const [search, setSearch] = useSearchParams();
  const tabParam = search.get('tab');
  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, retry] = useRetryKey();

  useEffect(() => {
    if (!me.authenticated) return;
    fetchProfile(handle)
      .then((p) => {
        setProfile(p);
        setError(null);
      })
      .catch((e) => setError(errorText(e)));
  }, [handle, me.authenticated, attempt]);

  if (!profile) {
    return (
      <Shell title={handle}>
        {error ? <PageProblem error={null} text={error} onRetry={retry} /> : <PageLoading />}
      </Shell>
    );
  }

  const matches = profile.matches ?? [];
  const playedSessions = profile.played ?? [];
  const byCampaign = new Map<string, { title: string; code: string | null; medals: string[] }>();
  for (const s of profile.standings) {
    if (s.medals.length === 0) continue;
    const key = s.campaignCode ?? s.campaignTitle;
    const group = byCampaign.get(key) ?? {
      title: s.campaignTitle,
      code: s.campaignCode,
      medals: [],
    };
    group.medals.push(...s.medals);
    byCampaign.set(key, group);
  }
  const medalCount = profile.standings.reduce((n, s) => n + s.medals.length, 0);
  const attendance = profile.standings.reduce((n, s) => n + s.attended, 0);
  const canSeeHistory = profile.mine || me.roles.includes('ADMIN');
  const tabs: Tab[] = (['played', 'made'] as Tab[])
    .concat(SHOW_MEDALS ? ['medals'] : [])
    .concat(canSeeHistory ? ['history'] : []);
  const tab: Tab = tabs.includes(tabParam as Tab) ? (tabParam as Tab) : 'played';
  const tabLabel: Record<Tab, string> = {
    played: ca.profilePage.tabPlayed,
    made: ca.made,
    medals: ca.medals,
    history: ca.historyTab,
  };
  const made = profile.made.length + profile.campaigns.length;
  // The header's figures are the platform's count, which leaves its own tests
  // out; an older platform sends none and the lists are counted as before.
  const sessionsPlayed = profile.sessionsPlayed ?? matches.length;
  const missionsMade = profile.missionsMade ?? made;
  const name = profile.displayName || profile.handle;

  return (
    <Shell
      title={
        <span className="person-title">
          <Avatar url={profile.avatarUrl} name={name} size={56} />
          {name}
        </span>
      }
      kicker={`@${profile.handle}`}
      chips={
        <>
          {profile.roles.map((r) => (
            <span key={r} className="chip">
              {(ca.roleNames as Record<string, string>)[r] ?? r}
            </span>
          ))}
          <span className="muted small">{ca.joined(fmtDate(profile.joinedAt))}</span>
        </>
      }
      actions={
        profile.mine && (
          <Link className="button" to="/settings">
            <i className="bi bi-gear" aria-hidden="true" /> {ca.nav.settings}
          </Link>
        )
      }
    >
      <div className="profile-page">
        {error && (
          <p className="notice bad" role="alert">
            {error}
          </p>
        )}
        <dl className="stats">
          <div className="stat">
            <dt>{ca.profilePage.sessionsPlayed}</dt>
            <dd>{sessionsPlayed}</dd>
          </div>
          <div className="stat">
            <dt>{ca.profilePage.missionsMade}</dt>
            <dd>{missionsMade}</dd>
          </div>
          {SHOW_MEDALS && (
            <div className="stat">
              <dt>{ca.medals}</dt>
              <dd>{medalCount}</dd>
            </div>
          )}
          <div className="stat">
            <dt>{ca.profilePage.attendance}</dt>
            <dd>{attendance}</dd>
          </div>
        </dl>
        {profile.mine && !profile.steamLinked && (
          <p className="muted small steam-nudge">
            <i className="bi bi-steam" aria-hidden="true" /> {ca.steamNudge}{' '}
            <Link to="/settings">{ca.profileUi.configure}</Link>
          </p>
        )}
        <nav className="tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={tab === t ? 'active' : ''}
              onClick={() => setSearch(t === 'played' ? {} : { tab: t }, { replace: true })}
            >
              <i className={`bi bi-${TAB_ICONS[t]}`} aria-hidden="true" />
              {tabLabel[t]}
            </button>
          ))}
        </nav>
        {tab === 'played' && (
          <section className="cards">
            <article className="card" aria-label={ca.profilePage.playedTitle}>
              <h2>{ca.profilePage.playedTitle}</h2>
              {playedSessions.length === 0 ? (
                <div className="empty">
                  <i className="bi bi-play-circle" aria-hidden="true" />
                  <p>{ca.profileUi.sessionsPlayedEmpty}</p>
                </div>
              ) : (
                <ul className="list played-sessions">
                  {playedSessions.map((s) => (
                    <li key={s.code}>
                      <span className="grow">
                        <Link to={`/s/${s.code}`}>
                          <strong>{s.title}</strong>
                        </Link>
                        {s.test && (
                          <>
                            {' '}
                            <span className="chip" title={ca.profilePage.notCountedHint}>
                              {ca.profilePage.notCounted}
                            </span>
                          </>
                        )}
                      </span>
                      {s.side && <span className="chip">{s.side}</span>}
                      <span className={`chip ${s.state === 'LIVE' ? 'ok' : ''}`}>
                        {ca.sessionStates[s.state] ?? s.state}
                      </span>
                      {s.reportUrl && (
                        <a className="small" href={s.reportUrl} target="_blank" rel="noreferrer">
                          {ca.profileUi.report}
                        </a>
                      )}
                      {s.startedAt && <span className="muted small">{fmtDate(s.startedAt)}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </article>
            <article className="card" aria-label={ca.profilePage.matchesTitle}>
              <h2>
                {ca.profilePage.matchesTitle} <PlannedChip id="profile-squad-role" named />
              </h2>
              {matches.length === 0 ? (
                <div className="empty">
                  <i className="bi bi-play-circle" aria-hidden="true" />
                  <p>{ca.profileUi.playedEmpty}</p>
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{ca.profileUi.colMission}</th>
                        <th>{ca.profileUi.colWorld}</th>
                        <th>{ca.profileUi.colSide}</th>
                        <th>{ca.profileUi.colTime}</th>
                        <th>{ca.profileUi.colDate}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches.map((m) => (
                        <tr key={m.id}>
                          <td>
                            {/* The title is what is read; the panel's file name stays as a hint. */}
                            <strong title={m.missionName ?? undefined}>
                              {m.displayTitle ?? m.missionName ?? '?'}
                            </strong>
                            {m.test && (
                              <>
                                {' '}
                                <span className="chip" title={ca.profilePage.notCountedHint}>
                                  {ca.profilePage.notCounted}
                                </span>
                              </>
                            )}
                            {m.reportUrl && (
                              <>
                                {' '}
                                <a
                                  className="small"
                                  href={m.reportUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {ca.profileUi.report}
                                </a>
                              </>
                            )}
                          </td>
                          <td className="muted small">{m.world ?? '—'}</td>
                          <td>{m.side ? <span className="chip">{m.side}</span> : '—'}</td>
                          <td className="num">{fmtTime(m.secondsPlayed)}</td>
                          <td className="muted small">
                            {m.startedAt ? fmtDate(m.startedAt) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </article>
            <article className="card" aria-label={ca.profilePage.directedTitle}>
              <h2>{ca.profilePage.directedTitle}</h2>
              <p className="muted small">{ca.decisions(profile.ballotsCast)}</p>
              {profile.directed.length === 0 ? (
                <div className="empty">
                  <i className="bi bi-compass" aria-hidden="true" />
                  <p>{ca.profileUi.directedEmpty}</p>
                </div>
              ) : (
                <ul className="list">
                  {profile.directed.map((s) => (
                    <li key={s.code}>
                      <Link to={`/s/${s.code}`}>{s.code}</Link>
                      <span className={`chip ${s.state === 'LIVE' ? 'ok' : ''}`}>
                        {ca.sessionStates[s.state] ?? s.state}
                      </span>
                      {s.outcome && <span className="muted small">{s.outcome}</span>}
                      <span className="grow" />
                      {s.startedAt && <span className="muted small">{fmtDate(s.startedAt)}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          </section>
        )}
        {tab === 'made' && (
          <section className="cards">
            <article className="card" aria-label={ca.made}>
              <h2>{ca.made}</h2>
              {made === 0 ? (
                <div className="empty">
                  <i className="bi bi-pencil-square" aria-hidden="true" />
                  <p>{ca.profileUi.madeEmpty}</p>
                </div>
              ) : (
                <ul className="list">
                  {profile.made.map((m) => (
                    <li key={`m-${m.code}`}>
                      <span className="grow">
                        <Link to={`/m/${m.code}`}>{m.title}</Link>{' '}
                        <span className="muted small">
                          {ca.profileUi.missionKind} · {m.worldName}
                        </span>
                      </span>
                      {m.test && (
                        <span className="chip" title={ca.profilePage.notCountedHint}>
                          {ca.profilePage.notCounted}
                        </span>
                      )}
                      <span className={`chip ${VISIBILITY_CHIP[m.visibility] ?? ''}`}>
                        {ca.visibilityNames[m.visibility] ?? m.visibility}
                      </span>
                    </li>
                  ))}
                  {profile.campaigns.map((c) => (
                    <li key={`c-${c.code}`}>
                      <span className="grow">
                        <Link to={`/c/${c.code}`}>{c.title}</Link>{' '}
                        <span className="muted small">
                          {ca.campaign} · {c.worldName} · {ca.chapter(c.chapter)}
                        </span>
                      </span>
                      <span className="chip">{c.state}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
            {profile.coEdited.length > 0 && (
              <article className="card" aria-label={ca.coEdited}>
                <h2>{ca.coEdited}</h2>
                <ul className="list">
                  {profile.coEdited.map((m) => (
                    <li key={m.code}>
                      <span className="grow">
                        <Link to={`/m/${m.code}`}>{m.title}</Link>{' '}
                        <span className="muted small">{m.worldName}</span>
                      </span>
                      <span className={`chip ${VISIBILITY_CHIP[m.visibility] ?? ''}`}>
                        {ca.visibilityNames[m.visibility] ?? m.visibility}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            )}
          </section>
        )}
        {SHOW_MEDALS && tab === 'medals' && (
          <section className="card" aria-label={ca.medals}>
            <h2>{ca.medals}</h2>
            {byCampaign.size === 0 ? (
              <div className="empty">
                <i className="bi bi-award" aria-hidden="true" />
                <p>{ca.profileUi.medalsEmpty}</p>
              </div>
            ) : (
              <ul className="list">
                {[...byCampaign.entries()].map(([key, group]) => (
                  <li key={key} className="medal-group">
                    <span className="campaign">
                      {group.code ? (
                        <Link to={`/c/${group.code}`}>{group.title}</Link>
                      ) : (
                        group.title
                      )}
                    </span>
                    <span className="row">
                      {group.medals.map((medal, i) => (
                        <span key={i} className="chip ok">
                          <i className="bi bi-award" aria-hidden="true" /> {medal}
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        {tab === 'history' && (
          <section className="card" aria-label={ca.historyTab}>
            <h2>{ca.historyTab}</h2>
            {profile.history.length === 0 ? (
              <div className="empty">
                <i className="bi bi-clock-history" aria-hidden="true" />
                <p>{ca.profileUi.historyEmpty}</p>
              </div>
            ) : (
              byDay(profile.history).map(([day, entries]) => (
                <Fragment key={day}>
                  <h2 className="section">{day}</h2>
                  <ul className="list history">
                    {entries.map((h, i) => (
                      <li key={`${day}-${i}`} title={h.kind}>
                        <span className="muted small when">{fmtClock(h.occurredAt)}</span>
                        {h.text ? (
                          <span className="grow">{h.text}</span>
                        ) : (
                          <>
                            <span className="chip kind">{h.kind}</span>
                            <span className="grow">{h.subject ?? ''}</span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </Fragment>
              ))
            )}
          </section>
        )}
        <Planned ids={['profile-campaign-rank']} />
      </div>
    </Shell>
  );
}
